import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import Login, { Register } from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import Parties from "@/pages/Parties";
import Products from "@/pages/Products";
import Documents from "@/pages/Documents";
import DocumentEditor from "@/pages/DocumentEditor";
import DocumentDetail from "@/pages/DocumentDetail";
import Payments from "@/pages/Payments";
import Expenses from "@/pages/Expenses";
import Inventory from "@/pages/Inventory";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";

function Protected() {
  const { session } = useAuth();
  if (session === null) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-500" data-testid="auth-loading">Loading your workspace…</div>;
  if (!session) return <Navigate to="/login" replace />;
  if (!session.org) return <div className="flex min-h-screen items-center justify-center p-6 text-center text-sm text-slate-600" data-testid="no-org-state">Your account has no active organization membership. Ask your admin for an invite.</div>;
  return <Outlet />;
}

function Guard({ perm, children }) {
  const { can } = useAuth();
  if (!can(perm)) return <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900" data-testid="unauthorized-state"><b>Not authorized.</b> Your role does not include <span className="num">{perm}</span>. Ask an admin to adjust your permissions.</div>;
  return children;
}

const DOC_KINDS = ["invoices", "bills", "sales-returns", "purchase-returns"];

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route element={<Protected />}>
            <Route element={<AppShell />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/customers" element={<Parties kind="customers" />} />
              <Route path="/suppliers" element={<Parties kind="suppliers" />} />
              <Route path="/products" element={<Products />} />
              {DOC_KINDS.map((k) => (
                <Route key={k}>
                  <Route path={`/${k}`} element={<Documents kind={k} />} />
                  <Route path={`/${k}/new`} element={<DocumentEditor kind={k} />} />
                  <Route path={`/${k}/:id`} element={<DocumentDetail kind={k} />} />
                  <Route path={`/${k}/:id/edit`} element={<DocumentEditor kind={k} />} />
                </Route>
              ))}
              <Route path="/payments" element={<Payments />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/reports" element={<Guard perm="reports:view"><Reports /></Guard>} />
              <Route path="/settings" element={<Guard perm="settings:manage"><Settings /></Guard>} />
              <Route path="/audit" element={<Guard perm="audit:view"><Audit /></Guard>} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
